import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, LinearProgress, Chip,
  CircularProgress, Alert, Accordion, AccordionSummary, AccordionDetails, Grid,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { projectsAPI, engineeringTasksAPI } from '../../services/api';

const DETAIL_STATUSES = ['offer_approved', 'in_progress', 'commissioning'];

const STATUS_LABELS = {
  opportunity: 'Oportunidad', conceptual: 'Ing. Conceptual',
  offer_preparation: 'Preparando Oferta', offer_sent: 'Oferta Enviada',
  offer_approved: 'Oferta Aprobada', in_progress: 'En Ejecucion',
  commissioning: 'Puesta en Marcha', completed: 'Completado',
};

const TASK_STATUS_COLORS = {
  pending: 'default', in_progress: 'primary', review: 'warning', completed: 'success',
};

const TASK_STATUS_LABELS = {
  pending: 'Pendiente', in_progress: 'En curso', review: 'En revision', completed: 'Completado',
};

export default function PlanningOverview() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [projectTasks, setProjectTasks] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const resp = await projectsAPI.list();
      const detailProjects = resp.data.filter((p) => DETAIL_STATUSES.includes(p.status));
      setProjects(detailProjects);

      const tasksMap = {};
      await Promise.all(
        detailProjects.map(async (p) => {
          try {
            const tResp = await engineeringTasksAPI.list(p.id);
            tasksMap[p.id] = tResp.data;
          } catch {
            tasksMap[p.id] = [];
          }
        })
      );
      setProjectTasks(tasksMap);
      setError(null);
    } catch (err) {
      console.error('Error loading planning data:', err);
      setError('Error al cargar los datos de planning.');
    } finally {
      setLoading(false);
    }
  };

  const getProjectProgress = (projectId) => {
    const tasks = projectTasks[projectId] || [];
    if (tasks.length === 0) return 0;
    return (tasks.reduce((sum, t) => sum + (parseFloat(t.progress) || 0), 0) / tasks.length).toFixed(0);
  };

  const getDisciplines = (projectId) => {
    const tasks = projectTasks[projectId] || [];
    return [...new Set(tasks.map((t) => t.discipline).filter(Boolean))];
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="body1" color="textSecondary" gutterBottom>
        Vision general de todos los proyectos en fase de ingenieria de detalle y ejecucion.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {projects.length === 0 ? (
        <Paper sx={{ p: 4, mt: 2, textAlign: 'center' }}>
          <Typography color="textSecondary">
            No hay proyectos en fase de ingenieria de detalle o ejecucion.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ mt: 2 }}>
          {/* Summary table */}
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#c62828' }}>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Proyecto</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Nombre</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Tareas</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff', width: '30%' }}>Avance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projects.map((p) => {
                  const tasks = projectTasks[p.id] || [];
                  const progress = getProjectProgress(p.id);
                  const completedTasks = tasks.filter((t) => t.status === 'completed').length;
                  return (
                    <TableRow key={p.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/projects/${p.id}`)}>
                      <TableCell><strong>{p.code}</strong></TableCell>
                      <TableCell>{p.name}</TableCell>
                      <TableCell><Chip label={STATUS_LABELS[p.status]} size="small" color="primary" /></TableCell>
                      <TableCell>{completedTasks}/{tasks.length}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress variant="determinate" value={parseFloat(progress)} sx={{ flexGrow: 1, height: 8, borderRadius: 4 }} />
                          <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 35 }}>{progress}%</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Detailed per-project accordion */}
          {projects.map((p) => {
            const tasks = projectTasks[p.id] || [];
            const progress = getProjectProgress(p.id);
            const disciplines = getDisciplines(p.id);

            return (
              <Accordion key={p.id} defaultExpanded={projects.length <= 3}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', mr: 2 }}>
                    <Typography fontWeight="bold">{p.code}</Typography>
                    <Typography sx={{ flexGrow: 1 }}>{p.name}</Typography>
                    <Typography variant="body2" fontWeight="bold">{progress}%</Typography>
                    <LinearProgress variant="determinate" value={parseFloat(progress)} sx={{ width: 120, height: 8, borderRadius: 4 }} />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {/* Discipline summary */}
                  {disciplines.length > 0 && (
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {disciplines.map((d) => {
                        const dTasks = tasks.filter((t) => t.discipline === d);
                        const dProgress = (dTasks.reduce((s, t) => s + (parseFloat(t.progress) || 0), 0) / dTasks.length).toFixed(0);
                        const dCompleted = dTasks.filter((t) => t.status === 'completed').length;
                        return (
                          <Grid item xs={6} sm={4} md={2} key={d}>
                            <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                              <Typography variant="caption" color="textSecondary">{d}</Typography>
                              <Typography variant="h6" fontWeight="bold">{dProgress}%</Typography>
                              <LinearProgress variant="determinate" value={parseFloat(dProgress)} sx={{ height: 4, borderRadius: 2, mb: 0.5 }} />
                              <Typography variant="caption">{dCompleted}/{dTasks.length}</Typography>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}

                  {/* Task list */}
                  {tasks.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">No hay tareas definidas para este proyecto.</Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#fafafa' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Ref.</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Tarea</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Disciplina</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Responsable</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Avance</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tasks.map((t) => (
                          <TableRow key={t.id} hover>
                            <TableCell>{t.document_ref || '-'}</TableCell>
                            <TableCell>{t.name}</TableCell>
                            <TableCell>{t.discipline || '-'}</TableCell>
                            <TableCell>{t.assigned_to || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={TASK_STATUS_LABELS[t.status] || t.status}
                                size="small"
                                color={TASK_STATUS_COLORS[t.status] || 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinearProgress variant="determinate" value={parseFloat(t.progress) || 0} sx={{ flexGrow: 1, height: 6, borderRadius: 3 }} />
                                <Typography variant="caption">{t.progress || 0}%</Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
